import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner, Badge } from 'react-bootstrap';
import {
  FaDownload,
  FaCalendarAlt,
  FaImages,
  FaFolder,
  FaPlus,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import './styles.css';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';

const ModalDiretorios = ({
  show,
  onHide,
  modalType,
  empreendimentoNome,
  codigoGrupoEmpresa,
}) => {
  const [albuns, setAlbuns] = useState([]);
  const [albumSelecionado, setAlbumSelecionado] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingArquivos, setLoadingArquivos] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [showNovoAlbumForm, setShowNovoAlbumForm] = useState(false);
  const [nomeNovoAlbum, setNomeNovoAlbum] = useState('');
  const [validadeNovoAlbum, setValidadeNovoAlbum] = useState('');
  const [comValidade, setComValidade] = useState(false);
  const fileInputRef = useRef(null);

  const getCategoria = () => {
    switch (modalType) {
      case 'LOGO':
        return 'logotipo';
      case 'FOTOS':
        return 'fotos-local';
      case 'PUBLICIDADE':
        return 'material';
      default:
        return null;
    }
  };

  useEffect(() => {
    if (show && codigoGrupoEmpresa) {
      carregarDados();
    }
  }, [show, codigoGrupoEmpresa, modalType]);

  const carregarDados = async () => {
    const categoria = getCategoria();
    if (!categoria) return;

    setLoading(true);
    try {
      const data = await api.SismacListar(categoria, codigoGrupoEmpresa);
      setAlbuns(data.albums || []);
      setArquivos([]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error(
        (error && error.message) || 'Erro ao carregar dados. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarAlbum = async album => {
    if (
      albumSelecionado &&
      albumSelecionado.caminho_completo === album.caminho_completo
    ) {
      setAlbumSelecionado(null);
      setArquivos([]);
      return;
    }

    const categoria = getCategoria();
    if (!categoria) return;

    setAlbumSelecionado(album);
    setLoadingArquivos(true);

    try {
      const arquivosData = await api.SismacListarAlbum(
        categoria,
        codigoGrupoEmpresa,
        album.caminho_completo
      );
      setArquivos(arquivosData || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos do álbum:', error);
      toast.error('Erro ao carregar arquivos do álbum.');
      setArquivos([]);
    } finally {
      setLoadingArquivos(false);
    }
  };

  const handleDownloadArquivo = async arquivo => {
    const categoria = getCategoria();
    if (!categoria || !albumSelecionado) return;

    try {
      toast.success(`Download iniciado: ${arquivo.nome}`);
      setDownloading({ [arquivo.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivoAlbum(
        categoria,
        codigoGrupoEmpresa,
        albumSelecionado.caminho_completo,
        arquivo.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download concluído!');
      setDownloading(prev => ({ ...prev, [arquivo.nome_completo]: false }));
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do arquivo.');
      setDownloading(prev => ({ ...prev, [arquivo.nome_completo]: false }));
    }
  };

  const handleDownloadAlbumZip = async () => {
    const categoria = getCategoria();
    if (!categoria || !albumSelecionado) return;

    try {
      const downloadData = await api.SismacDownloadAlbumZip(
        categoria,
        codigoGrupoEmpresa,
        albumSelecionado.caminho_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteCharacters);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download do álbum concluído!');
    } catch (error) {
      console.error('Erro ao fazer download do álbum:', error);
      toast.error('Erro ao fazer download do álbum.');
    }
  };

  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleCriarAlbum = async () => {
    const categoria = getCategoria();
    if (!categoria) return;

    if (!nomeNovoAlbum.trim()) {
      toast.error('Informe o nome do álbum!');
      return;
    }

    try {
      const data = { nome_album: nomeNovoAlbum.trim() };

      if (comValidade && validadeNovoAlbum) {
        const parts = validadeNovoAlbum.split('/');
        if (parts.length === 3) {
          const dia = parseInt(parts[0], 10);
          const mes = parseInt(parts[1], 10) - 1;
          const ano = parseInt(parts[2], 10);
          data.validade = new Date(ano, mes, dia).toISOString();
        }
      }

      await api.SismacCriarAlbum(categoria, codigoGrupoEmpresa, data);
      toast.success('Álbum criado com sucesso!');

      setNomeNovoAlbum('');
      setValidadeNovoAlbum('');
      setComValidade(false);
      setShowNovoAlbumForm(false);

      await carregarDados();
    } catch (error) {
      console.error('Erro ao criar álbum:', error);
      toast.error('Erro ao criar álbum.');
    }
  };

 const handleUploadArquivos = async event => {
   const categoria = getCategoria();
   if (!categoria || !albumSelecionado) return;

   const files = event.target.files;
   if (!files || files.length === 0) return;

   setUploading(true);

   try {
     for (let i = 0; i < files.length; i++) {
       const file = files[i];
       const ext = file.name.toLowerCase();
       const extensoesValidas = ['.jpg', '.jpeg', '.png', '.gif', '.bmp'];

       let extensaoValida = false;
       for (let j = 0; j < extensoesValidas.length; j++) {
         if (ext.endsWith(extensoesValidas[j])) {
           extensaoValida = true;
           break;
         }
       }

       if (!extensaoValida) {
         toast.error('Apenas arquivos de imagem são permitidos!');
         continue;
       }

       const formData = new FormData();
       formData.append('file', file);

       await api.SismacUploadArquivoAlbum(
         categoria,
         codigoGrupoEmpresa,
         albumSelecionado.caminho_completo,
         formData
       );

       toast.success(file.name + ' enviado com sucesso!');
     }

     await handleSelecionarAlbum(albumSelecionado);
   } catch (error) {
     console.error('Erro ao fazer upload:', error);
     toast.error('Erro ao fazer upload dos arquivos.');
   } finally {
     setUploading(false);
     if (fileInputRef.current) {
       fileInputRef.current.value = '';
     }
   }
 };

  const handleExcluirArquivo = async arquivo => {
    const categoria = getCategoria();
    if (!categoria || !albumSelecionado) return;

    try {
      await api.SismacExcluirArquivoAlbum(
        categoria,
        codigoGrupoEmpresa,
        albumSelecionado.caminho_completo,
        arquivo.nome_completo
      );
      toast.success('Arquivo excluído com sucesso!');
      await handleSelecionarAlbum(albumSelecionado);
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      toast.error('Erro ao excluir arquivo.');
    }
  };

  const handleExcluirAlbum = async (album, e) => {
    if (e) {
      e.stopPropagation();
    }

    const categoria = getCategoria();
    if (!categoria) return;

    try {
      await api.SismacExcluirAlbum(
        categoria,
        codigoGrupoEmpresa,
        album.caminho_completo
      );
      toast.success('Álbum excluído com sucesso!');

      if (
        albumSelecionado &&
        albumSelecionado.caminho_completo === album.caminho_completo
      ) {
        setAlbumSelecionado(null);
        setArquivos([]);
      }

      await carregarDados();
    } catch (error) {
      console.error('Erro ao excluir álbum:', error);
      toast.error('Erro ao excluir álbum.');
    }
  };

  const handleClose = () => {
    setAlbumSelecionado(null);
    setAlbuns([]);
    setArquivos([]);
    setShowNovoAlbumForm(false);
    setNomeNovoAlbum('');
    setValidadeNovoAlbum('');
    setComValidade(false);
    onHide();
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="sismac_modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ color: '#fff' }}>
          {modalType} - {empreendimentoNome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: '#0747a6' }} />
            <p className="mt-2" style={{ color: '#0747a6' }}>
              Carregando...
            </p>
          </div>
        ) : (
          <>
            <div className="sismac_section">
              <div className="sismac_section-header">
                <h6 className="sismac_section-title">
                  <FaFolder />
                  <span>Álbuns</span>
                  {albuns.length > 0 && (
                    <Badge bg="secondary">{albuns.length}</Badge>
                  )}
                </h6>
                {AuthStorage.temCaracteristica(
                  caracteristicas.GerencialSismac
                ) && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowNovoAlbumForm(!showNovoAlbumForm)}
                  >
                    <FaPlus className="mr-1" />

                    {showNovoAlbumForm ? 'Cancelar' : 'Criar Álbum'}
                  </Button>
                )}
              </div>

              {showNovoAlbumForm && (
                <div className="sismac_form-card">
                  <div className="sismac_form-row">
                    <div className="sismac_form-group">
                      <label className="sismac_form-label">Nome do álbum</label>
                      <input
                        type="text"
                        className="sismac_form-input"
                        placeholder="Digite o nome do álbum"
                        value={nomeNovoAlbum}
                        onChange={e => setNomeNovoAlbum(e.target.value)}
                      />
                    </div>
                    <div className="sismac_form-group">
                      <label className="sismac_form-label">
                        Validade (opcional)
                      </label>
                      <input
                        type="text"
                        className="sismac_form-input"
                        placeholder="DD/MM/AAAA"
                        value={validadeNovoAlbum}
                        onChange={e => setValidadeNovoAlbum(e.target.value)}
                        disabled={!comValidade}
                      />
                    </div>
                  </div>
                  <div className="sismac_form-checkbox">
                    <input
                      type="checkbox"
                      id="comValidade"
                      checked={comValidade}
                      onChange={e => setComValidade(e.target.checked)}
                    />
                    <label htmlFor="comValidade">Álbum com validade?</label>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCriarAlbum}
                  >
                    Criar Álbum
                  </Button>
                </div>
              )}

              {albuns.length === 0 ? (
                <div className="sismac_empty sismac_empty--card">
                  <FaImages size={32} className="sismac_empty-icon" />
                  <p className="sismac_empty-text">Nenhum álbum encontrado.</p>
                </div>
              ) : (
                <div className="sismac_albums">
                  {albuns.map((album, index) => {
                    const isSelected =
                      albumSelecionado &&
                      albumSelecionado.caminho_completo ===
                        album.caminho_completo;
                    return (
                      <div key={index} className="sismac_album">
                        <button
                          className={
                            'sismac_album-badge' +
                            (isSelected ? ' sismac_album-badge--selected' : '')
                          }
                          onClick={() => handleSelecionarAlbum(album)}
                        >
                          <FaFolder />
                          <span>{album.caminho_completo}</span>
                          {album.data_validade && <FaCalendarAlt size={12} />}
                        </button>
                        {AuthStorage.temCaracteristica(
                          caracteristicas.GerencialSismac
                        ) && (
                          <Button
                            variant="danger"
                            size="sm"
                            className="sismac_album-delete"
                            onClick={e => handleExcluirAlbum(album, e)}
                            title="Excluir álbum"
                          >
                            <FaTrash
                              size={10}
                              className="sismac_album-delete-icon"
                            />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {albumSelecionado && (
              <div className="sismac_section">
                <div className="sismac_section-header">
                  <h6 className="sismac_section-title">
                    <FaImages />
                    <span>Arquivos: {albumSelecionado.caminho_completo}</span>
                    {arquivos.length > 0 && (
                      <Badge bg="secondary">{arquivos.length}</Badge>
                    )}
                  </h6>
                  <div className="sismac_section-actions">
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept=".jpg,.jpeg,.png,.gif,.bmp"
                      multiple
                      onChange={handleUploadArquivos}
                    />
                    {AuthStorage.temCaracteristica(
                      caracteristicas.GerencialSismac
                    ) && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleFileInputClick}
                        disabled={uploading}
                      >
                        <FaPlus className="mr-1" />
                        {uploading ? 'Enviando...' : 'Adicionar fotos'}
                      </Button>
                    )}
                    {arquivos.length > 0 && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleDownloadAlbumZip}
                      >
                        <FaDownload className="mr-1" />
                        Baixar Álbum (ZIP)
                      </Button>
                    )}
                  </div>
                </div>

                {loadingArquivos ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <p className="mt-2 small text-muted">
                      Carregando arquivos...
                    </p>
                  </div>
                ) : arquivos.length === 0 ? (
                  <div className="sismac_empty sismac_empty--card">
                    <FaImages size={32} className="sismac_empty-icon" />
                    <p className="sismac_empty-text">
                      Nenhum arquivo neste álbum.
                    </p>
                  </div>
                ) : (
                  <div className="sismac_files-grid">
                    {arquivos.map((arquivo, index) => (
                      <div key={index} className="sismac_file-card">
                        {AuthStorage.temCaracteristica(
                          caracteristicas.GerencialSismac
                        ) && (
                          <Button
                            variant="danger"
                            size="sm"
                            className="sismac_file-card-delete"
                            onClick={() => handleExcluirArquivo(arquivo)}
                            title="Excluir arquivo"
                          >
                            <FaTrash
                              size={12}
                              className="sismac_card-delete-icon"
                            />
                          </Button>
                        )}
                        <div className="sismac_file-card-icon">
                          <FaImages size={40} />
                        </div>
                        <p
                          className="sismac_file-card-name"
                          title={arquivo.nome}
                        >
                          {arquivo.nome_arquivo}
                        </p>
                        <p className="sismac_file-card-size">
                          {arquivo.tamanho_formatado}
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleDownloadArquivo(arquivo)}
                          disabled={downloading[arquivo.nome_completo]}
                        >
                          <FaDownload className="mr-1" />
                          {downloading[arquivo.nome_completo]
                            ? 'Baixando...'
                            : 'Download'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDiretorios;
